import {
  Person,
  SwapHoriz,
  Download,
  Visibility,
  GetApp,
  Description,
  Add,
  Schedule,
  Edit,
  Send,
} from '@mui/icons-material';
import { CardContainer, Dropdown, Button, DataTable, Tag } from 'finara-components';
import type { DropdownOption, DataTableColumn } from 'finara-components';
import { useState } from 'react';

import { CsvExportButton } from '@/components/common';
import {
  companies,
  reportTemplates,
  generatedReports,
  unifiedMovements,
  complianceDocuments,
  scheduledReports,
  GeneratedReport,
  UnifiedMovement,
  ComplianceDocument,
  ScheduledReport,
} from '@/data/super-admin/reports/reportsData';

export const WorkerReportsTab: React.FC = () => {
  const [selectedCompany, setSelectedCompany] = useState('');

  const companyOptions: DropdownOption[] = companies.map(company => ({
    value: company.id,
    label: company.name,
  }));

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusVariant = (status: string): 'green' | 'blue' => {
    return status === 'Éxito' ? 'green' : 'blue';
  };

  const generatedReportsColumns: DataTableColumn<GeneratedReport>[] = [
    {
      key: 'name',
      label: 'Nombre',
      render: (_, report) => <div className="font-medium text-brand-white">{report.name}</div>,
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (_, report) => <div className="text-sm text-muted">{report.type}</div>,
    },
    {
      key: 'format',
      label: 'Formato',
      render: (_, report) => <div className="text-sm text-muted">{report.format}</div>,
    },
    {
      key: 'generatedBy',
      label: 'Generado por',
      render: (_, report) => <div className="text-sm text-muted">{report.generatedBy}</div>,
    },
    {
      key: 'generatedAt',
      label: 'Fecha',
      render: (_, report) => <div className="text-sm text-muted">{report.generatedAt}</div>,
    },
    {
      key: 'status',
      label: 'Estado',
      render: (_, report) => (
        <Tag variant={report.status === 'Listo' ? 'green' : 'blue'}>{report.status}</Tag>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: () => (
        <div className="flex items-center space-x-2">
          <button
            className="text-muted hover:text-muted hover:cursor-pointer p-1 rounded-md"
            title="Descargar"
          >
            <Download fontSize="small" />
          </button>
        </div>
      ),
    },
  ];

  const movementsColumns: DataTableColumn<UnifiedMovement>[] = [
    {
      key: 'date',
      label: 'Fecha',
      render: (_, movement) => <div className="text-sm text-brand-white">{movement.date}</div>,
    },
    {
      key: 'company',
      label: 'Empresa',
      render: (_, movement) => <div className="text-sm text-brand-white">{movement.company}</div>,
    },
    {
      key: 'worker',
      label: 'Trabajador',
      render: (_, movement) => (
        <div className="text-sm text-brand-white">{movement.worker || '-'}</div>
      ),
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (_, movement) => <div className="text-sm text-brand-white">{movement.type}</div>,
    },
    {
      key: 'amount',
      label: 'Monto',
      render: (_, movement) => (
        <div className="font-medium text-brand-white">{formatCurrency(movement.amount)}</div>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (_, movement) => (
        <Tag variant={getStatusVariant(movement.status)}>{movement.status}</Tag>
      ),
    },
    {
      key: 'reference',
      label: 'Referencia',
      render: (_, movement) => <div className="text-sm text-brand-white">{movement.reference}</div>,
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: () => (
        <div className="flex items-center space-x-2">
          <button
            className="text-muted hover:text-muted hover:cursor-pointer p-1 rounded-md"
            title="Ver detalles"
          >
            <Visibility fontSize="small" />
          </button>
          <button
            className="text-muted hover:text-muted hover:cursor-pointer p-1 rounded-md"
            title="Descargar"
          >
            <Download fontSize="small" />
          </button>
          <button
            className="text-muted hover:text-muted hover:cursor-pointer p-1 rounded-md"
            title="Abrir"
          >
            <GetApp fontSize="small" />
          </button>
        </div>
      ),
    },
  ];

  const complianceColumns: DataTableColumn<ComplianceDocument>[] = [
    {
      key: 'name',
      label: 'Nombre',
      render: (_, doc) => <div className="font-medium text-brand-white">{doc.name}</div>,
    },
    {
      key: 'company',
      label: 'Empresa',
      render: (_, doc) => <div className="text-sm text-brand-white">{doc.company}</div>,
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (_, doc) => <div className="text-sm text-brand-white">{doc.type}</div>,
    },
    {
      key: 'status',
      label: 'Estado',
      render: (_, doc) => <Tag variant="blue">{doc.status}</Tag>,
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: () => (
        <div className="flex items-center space-x-2">
          <button
            className="text-muted hover:text-muted hover:cursor-pointer p-1 rounded-md"
            title="Ver"
          >
            <Visibility fontSize="small" />
          </button>
          <button
            className="text-muted hover:text-muted hover:cursor-pointer p-1 rounded-md"
            title="Descargar PDF"
          >
            <Download fontSize="small" />
          </button>
        </div>
      ),
    },
  ];

  const scheduledReportsColumns: DataTableColumn<ScheduledReport>[] = [
    {
      key: 'name',
      label: 'Nombre',
      render: (_, report) => <div className="font-medium text-brand-white">{report.name}</div>,
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (_, report) => <div className="text-sm text-muted">{report.type}</div>,
    },
    {
      key: 'frequency',
      label: 'Frecuencia',
      render: (_, report) => <div className="text-sm text-muted">{report.frequency}</div>,
    },
    {
      key: 'format',
      label: 'Formato',
      render: (_, report) => <div className="text-sm text-muted">{report.format}</div>,
    },
    {
      key: 'nextRun',
      label: 'Próxima Ejecución',
      render: (_, report) => <div className="text-sm text-muted">{report.nextRun}</div>,
    },
    {
      key: 'recipients',
      label: 'Destinatarios',
      render: (_, report) => <div className="text-sm text-muted">{report.recipients}</div>,
    },
    {
      key: 'status',
      label: 'Estado',
      render: (_, report) => <Tag variant="green">{report.status}</Tag>,
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: () => (
        <div className="flex items-center space-x-2">
          <button
            className="text-muted hover:text-muted hover:cursor-pointer p-1 rounded-md"
            title="Editar"
          >
            <Edit fontSize="small" />
          </button>
          <button
            className="text-muted hover:text-muted hover:cursor-pointer p-1 rounded-md"
            title="Enviar Ahora"
          >
            <Send fontSize="small" />
          </button>
        </div>
      ),
    },
  ];

  // Filter worker report templates
  const workerReportTemplates = reportTemplates.filter(template => template.type === 'worker');

  return (
    <div className="space-y-6">
      {/* Worker Selection */}
      <CardContainer>
        <div className="pb-6 border-b border-border flex items-center space-x-2">
          <Person />
          <h3 className="font-semibold text-brand-white">Seleccionar Trabajador</h3>
        </div>
        <div className="pt-6">
          <div className="mb-4">
            <label
              htmlFor="worker-company-select"
              className="block text-sm font-medium text-muted mb-2"
            >
              Empresas *
            </label>
            <Dropdown
              id="worker-company-select"
              value={selectedCompany}
              onChange={value => setSelectedCompany(String(value))}
              options={companyOptions}
              placeholder="Todas las empresas"
              className="w-full"
            />
          </div>
          {!selectedCompany && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-md p-3">
              <p className="text-sm text-yellow-400">
                Selecciona una empresa para generar reportes individuales de trabajadores.
              </p>
            </div>
          )}
        </div>
      </CardContainer>

      {/* Individual Reports */}
      <CardContainer>
        <div className="pb-6 border-b border-border">
          <h3 className="font-semibold text-brand-white">Reportes Individuales</h3>
        </div>
        <div className="pt-6">
          <div className="space-y-3">
            {workerReportTemplates.map(template => (
              <div
                key={template.id}
                className="flex items-center justify-between p-3 border border-border rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div>
                    <div className="font-medium text-brand-white">{template.name}</div>
                    <div className="text-sm text-muted">{template.description}</div>
                  </div>
                </div>
                <Button variant="white" size="small" disabled={!selectedCompany}>
                  Generar
                </Button>
              </div>
            ))}
          </div>
          {!selectedCompany && (
            <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-md p-3">
              <p className="text-sm text-yellow-400">
                Selecciona una empresa para generar reportes individuales de trabajadores.
              </p>
            </div>
          )}
        </div>
      </CardContainer>

      {/* Generated Reports */}
      <CardContainer>
        <div className="pb-6 border-b border-border">
          <h3 className="font-semibold text-brand-white">Reportes Generados</h3>
        </div>
        <div className="pt-6">
          <DataTable
            columns={generatedReportsColumns}
            data={generatedReports}
            responsive
            emptyMessage={
              <div className="text-center py-8">
                <Description className="text-muted mx-auto mb-4" style={{ fontSize: '3rem' }} />
                <h3 className="text-lg font-medium text-brand-white mb-2">
                  No hay reportes generados
                </h3>
                <p className="text-muted">
                  Genera tu primer reporte usando las plantillas disponibles.
                </p>
              </div>
            }
          />
        </div>
      </CardContainer>

      {/* Unified Movements */}
      <CardContainer>
        <div className="pb-6 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-brand-white">Movimientos Unificados</h3>
          <div className="flex items-center space-x-2">
            <CsvExportButton
              data={unifiedMovements}
              filename="movimientos_unificados_excel"
              columns={movement => ({
                Fecha: movement.date,
                Empresa: movement.company,
                Trabajador: movement.worker || '-',
                Tipo: movement.type,
                Monto: formatCurrency(movement.amount).replace(/\s/g, ''),
                Estado: movement.status,
                Referencia: movement.reference,
              })}
              variant="white"
              size="small"
              buttonText="Exportar Excel"
            />
            <CsvExportButton
              data={unifiedMovements}
              filename="movimientos_unificados"
              columns={movement => ({
                Fecha: movement.date,
                Empresa: movement.company,
                Trabajador: movement.worker || '-',
                Tipo: movement.type,
                Monto: formatCurrency(movement.amount).replace(/\s/g, ''),
                Estado: movement.status,
                Referencia: movement.reference,
              })}
              variant="white"
              size="small"
              buttonText="Exportar CSV"
            />
          </div>
        </div>
        <div className="pt-6">
          <DataTable
            columns={movementsColumns}
            data={unifiedMovements}
            responsive
            emptyMessage={
              <div className="text-center py-8">
                <SwapHoriz className="text-muted mx-auto mb-4" style={{ fontSize: '3rem' }} />
                <h3 className="text-lg font-medium text-brand-white mb-2">
                  No se encontraron movimientos
                </h3>
                <p className="text-muted">Los movimientos aparecerán aquí cuando se procesen.</p>
              </div>
            }
          />
        </div>
      </CardContainer>

      {/* Compliance Documents */}
      <CardContainer>
        <div className="pb-6 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-brand-white">Comprobantes & CEP</h3>
          <Button variant="white" size="small" startIcon={<Download />}>
            Descargar Lote (Filtros Activos)
          </Button>
        </div>
        <div className="pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-muted mb-4">Comprobantes Disponibles</h4>
              <DataTable
                columns={complianceColumns}
                data={complianceDocuments}
                responsive
                emptyMessage={
                  <div className="text-center py-8">
                    <Description
                      className="text-muted mx-auto mb-4"
                      style={{ fontSize: '3rem' }}
                    />
                    <h3 className="text-lg font-medium text-brand-white mb-2">
                      No hay comprobantes disponibles
                    </h3>
                    <p className="text-muted">Selecciona un comprobante para visualizar.</p>
                  </div>
                }
              />
            </div>
            <div>
              <h4 className="text-sm font-medium text-muted mb-4">Vista Previa PDF</h4>
              <div className="bg-white/5 border border-border rounded-lg p-8 text-center">
                <Description className="text-muted mx-auto mb-4" style={{ fontSize: '3rem' }} />
                <p className="text-muted">Selecciona un comprobante para visualizar</p>
              </div>
            </div>
          </div>
        </div>
      </CardContainer>

      {/* Report Scheduling */}
      <CardContainer>
        <div className="pb-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Schedule className="text-blue-600" />
            <h3 className="font-semibold text-brand-white">Programación de Reportes</h3>
          </div>
          <Button variant="black" startIcon={<Add />}>
            Nueva Programación
          </Button>
        </div>
        <div className="pt-6">
          <div className="mb-6">
            <h4 className="text-sm font-medium text-muted mb-4">Crear Tarea Recurrente</h4>
            <div className="grid grid-cols-1 md:!grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="frequency-select"
                  className="block text-sm font-medium text-muted mb-2"
                >
                  Frecuencia
                </label>
                <select className="w-full px-3 py-2 border border-border bg-surface text-brand-white rounded-md focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue">
                  <option>Semanal</option>
                  <option>Mensual</option>
                  <option>Trimestral</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="format-select"
                  className="block text-sm font-medium text-muted mb-2"
                >
                  Formato
                </label>
                <select className="w-full px-3 py-2 border border-border bg-surface text-brand-white rounded-md focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue">
                  <option>PDF</option>
                  <option>Excel</option>
                  <option>CSV</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label
                htmlFor="recipients-input"
                className="block text-sm font-medium text-muted mb-2"
              >
                Destinatarios
              </label>
              <input
                type="text"
                placeholder="email1@empresa.com, email2@empresa.com"
                className="w-full px-3 py-2 border border-border bg-surface text-brand-white rounded-md focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue"
              />
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-muted mb-4">Reportes Programados</h4>
            <DataTable
              columns={scheduledReportsColumns}
              data={scheduledReports}
              responsive
              emptyMessage={
                <div className="text-center py-8">
                  <Schedule className="text-muted mx-auto mb-4" style={{ fontSize: '3rem' }} />
                  <h3 className="text-lg font-medium text-brand-white mb-2">
                    No hay reportes programados
                  </h3>
                  <p className="text-muted">Crea tu primera programación automática.</p>
                </div>
              }
            />
          </div>
        </div>
      </CardContainer>
    </div>
  );
};
